import { PublicKey } from '@solana/web3.js'
import {
  Client,
  MarketState,
} from '@trathailoi/phoenix-orderbook-sdk'

import useOrderbookIds from '@/hooks/use-orderbook-ids'
import useQueryWithSubscription from '@/hooks/use-query-with-subscription'
import { useSettings } from '@/hooks/use-settings'
import { useConnection } from '@/wallet/adapter'

export type MarketInfoQueryResult = {
  phoenixClient?: Client,
  marketState?: MarketState,
  baseTokenMintAddress?: PublicKey,
  quoteTokenMintAddress?: PublicKey,
}

const useFetchMarketInfo = (marketAddress?: PublicKey, enabled?: boolean) => {
  const { connection } = useConnection()
  const { data: settingsData } = useSettings()
  const { programId, seatManagerProgramId } = useOrderbookIds()

  return useQueryWithSubscription('onAccountChange', {
    queryKey: [
      'marketState',
      marketAddress,
      programId.toBase58(),
      seatManagerProgramId.toBase58(),
    ],
    queryFn: async () => {
      if (!marketAddress) return

      const phoenixClient = await Client.createWithoutConfig(
        connection,
        programId,
        seatManagerProgramId,
        [marketAddress],
      )
      phoenixClient.printLadder(marketAddress.toBase58())

      const marketState = phoenixClient.marketStates.get(marketAddress.toBase58())
      const baseTokenMintAddress = marketState?.data.header.baseParams.mintKey
      const quoteTokenMintAddress = marketState?.data.header.quoteParams.mintKey

      const result: MarketInfoQueryResult = {
        phoenixClient,
        marketState,
        baseTokenMintAddress,
        quoteTokenMintAddress,
      }
      return result
    },
    refetchOnWindowFocus: false,
    enabled: enabled ?? Boolean(settingsData?.orderbook_feature_enabled && marketAddress),
    keepPreviousData: true,
    subscriptionOptions: marketAddress,
  })
}

export default useFetchMarketInfo
